import { inject, Injectable, NgZone, OnDestroy } from "@angular/core";

/**
 * Tracks page visibility state using the Page Visibility API.
 * Detects when the user returns after a prolonged period of inactivity
 * (e.g., laptop lid closed, tab hidden for a long time).
 */
@Injectable({ providedIn: "root" })
export class LgIdleTrackingService implements OnDestroy {
    private _ngZone = inject(NgZone);

    private _hiddenSince: number | null = null;
    private _lastIdleDurationMs = 0;
    private _becameVisibleAt = performance.now();

    private _boundHandler = this._onVisibilityChange.bind(this);

    constructor() {
        this._ngZone.runOutsideAngular(() => {
            document.addEventListener("visibilitychange", this._boundHandler);
        });

        if (document.hidden) {
            this._hiddenSince = performance.now();
        }
    }

    /**
     * Returns true if the page was hidden for at least `thresholdMs` milliseconds
     * and became visible again within the last `recencyWindowMs` milliseconds.
     *
     * @param thresholdMs Minimum hidden duration to count as "idle" (default: 5 minutes)
     * @param recencyWindowMs How recently the page must have become visible (default: 60 seconds)
     */
    wasRecentlyIdle(thresholdMs = 5 * 60 * 1000, recencyWindowMs = 60_000): boolean {
        if (this._lastIdleDurationMs < thresholdMs) {
            return false;
        }

        const elapsed = performance.now() - this._becameVisibleAt;
        return elapsed <= recencyWindowMs;
    }

    ngOnDestroy(): void {
        document.removeEventListener("visibilitychange", this._boundHandler);
    }

    private _onVisibilityChange(): void {
        if (document.hidden) {
            this._hiddenSince = performance.now();
        } else if (this._hiddenSince !== null) {
            const now = performance.now();
            this._lastIdleDurationMs = now - this._hiddenSince;
            this._becameVisibleAt = now;
            this._hiddenSince = null;
        }
    }
}
